import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthenticatedLayout from "../../components/layout/AuthenticatedLayout";
import { CreatePostHeader } from "../create-post/components/CreatePostHeader";
import { CreatePostUserInfo } from "../create-post/components/CreatePostUserInfo";
import { CreatePostContent } from "../create-post/components/CreatePostContent";
import { CreatePostMediaOptions } from "../create-post/components/CreatePostMediaOptions";
import { CreatePostTypeSelector } from "../create-post/components/CreatePostTypeSelector";
import UploadMedia from "../create-post/components/UploadMedia";
import { mockPosts } from "@/lib/mock-data/posts";

type PostType = "Chia sẻ" | "Sự kiện" | "Cảnh báo" | "Cho" | "Nhờ" | "Cần";

const BottomSheet: React.FC<{
  onUpload: (files: File[]) => void;
  selectedTag: PostType | null;
  onSelectTag: (tag: PostType) => void;
}> = ({ onUpload, selectedTag, onSelectTag }) => (
  <div className="w-full max-w-md mx-auto px-0 pt-2 flex flex-col gap-3 z-10">
    <CreatePostMediaOptions onUpload={onUpload} />
    <CreatePostTypeSelector
      selectedTag={selectedTag}
      onSelectTag={onSelectTag}
    />
  </div>
);

const EditPostPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [media, setMedia] = useState<string[]>([]); // store image URLs
  const [selectedTag, setSelectedTag] = useState<PostType | null>(null);
  const [content, setContent] = useState("");

  useEffect(() => {
    // Lấy post từ localStorage hoặc mockPosts
    let found = mockPosts.find((p) => p.id === id) || null;
    if (!found) {
      const local = JSON.parse(localStorage.getItem("locuno_posts") || "[]");
      found =
        local.find((p: { id: string }) => String(p.id) === String(id)) || null;
    }
    if (found) {
      setContent(found.content || "");
      setMedia(
        found.media?.map((m: string | { url: string }) =>
          typeof m === "string" ? m : m.url,
        ) || [],
      );
      // Map category to PostType
      const categoryToPostType = (category: string): PostType | null => {
        switch (category) {
          case "help":
            return "Cần";
          case "alert":
            return "Cảnh báo";
          case "share":
            return "Chia sẻ";
          case "event":
            return "Sự kiện";
          case "give":
            return "Cho";
          case "request":
            return "Nhờ";
          default:
            return null;
        }
      };
      setSelectedTag(
        found.category ? categoryToPostType(found.category) : null,
      );
    }
  }, [id]);

  const handleUpload = (files: File[]) => {
    Promise.all(
      files.map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }),
    ).then((urls) => setMedia(urls));
  };

  const handleEditPost = () => {
    if (!selectedTag || !content.trim()) return;
    // Cập nhật post trong localStorage
    const posts = JSON.parse(localStorage.getItem("locuno_posts") || "[]");
    const idx = posts.findIndex(
      (p: { id: string }) => String(p.id) === String(id),
    );
    if (idx !== -1) {
      posts[idx] = {
        ...posts[idx],
        content,
        media,
        tag: selectedTag,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem("locuno_posts", JSON.stringify(posts));
    }
    navigate(`/posts/${id}`);
  };

  const isDisabled = !selectedTag || !content.trim();

  return (
    <AuthenticatedLayout
      showBottomNav={false}
      showNavbar={false}
      backgroundColor="#FBF6F1"
    >
      <div className="min-h-screen flex flex-col max-w-md mx-auto justify-end">
        <div>
          <CreatePostHeader
            onSubmit={handleEditPost}
            disabled={isDisabled}
            submitText="Chỉnh sửa"
          />
          <CreatePostUserInfo />
          <CreatePostContent
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>
        <div className="flex flex-col w-full mt-auto">
          <UploadMedia media={media} />
          <div className="w-full flex flex-col items-end">
            <BottomSheet
              onUpload={handleUpload}
              selectedTag={selectedTag}
              onSelectTag={setSelectedTag}
            />
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default EditPostPage;
